type Field = HTMLInputElement | HTMLTextAreaElement;

const NON_TEXT_INPUT_TYPES = [
    'button',
    'checkbox',
    'color',
    'file',
    'hidden',
    'image',
    'radio',
    'range',
    'reset',
    'search',
    'submit',
];

function isTextInput(element: Element): element is HTMLInputElement {
    return (
        'INPUT' === element.tagName &&
        !NON_TEXT_INPUT_TYPES.includes((element as HTMLInputElement).type)
    );
}

function isFillable(element: Element): element is Field {
    const field = element as Field;

    return (
        (isTextInput(element) || 'TEXTAREA' === element.tagName) &&
        !field.disabled &&
        !field.readOnly &&
        (field.checkVisibility?.() ?? true)
    );
}

function nextField(input: HTMLInputElement): Field | undefined {
    const elements = Array.from(input.form?.elements ?? []);

    return elements
        .slice(elements.indexOf(input) + 1)
        .find((e): e is Field => isFillable(e));
}

// Phone keyboards show Enter as the action key: in a form with several fields it
// moves to the next one, as in native apps, and only the last field submits.
export function moveToNextFieldOnEnter(event: KeyboardEvent): void {
    const input = event.target as Element | null;
    if (
        'Enter' !== event.key ||
        event.defaultPrevented ||
        event.isComposing ||
        !input ||
        !isTextInput(input) ||
        !input.form
    ) {
        return;
    }

    const next = nextField(input);
    if (next) {
        event.preventDefault();
        next.focus();
    }
}

export function labelEnterKey(event: FocusEvent): void {
    const input = event.target as Element | null;
    if (!input || !isTextInput(input) || !input.form) {
        return;
    }

    input.enterKeyHint = nextField(input) ? 'next' : 'done';
}

export function installFormEnterKeyNavigation(): void {
    document.addEventListener('keydown', moveToNextFieldOnEnter);
    document.addEventListener('focusin', labelEnterKey);
}
